
import React from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CalendarEvent } from '@/hooks/use-chart-data';
import { getProductColor } from '@/utils/colorUtils';

interface HarvestProductTimelineProps {
  events: CalendarEvent[];
}

const HarvestProductTimeline: React.FC<HarvestProductTimelineProps> = ({ events }) => {
  // Group events by product
  const productGroups = events.reduce((groups, event) => {
    if (!groups[event.product]) {
      groups[event.product] = [];
    }
    groups[event.product].push(event);
    return groups;
  }, {} as Record<string, CalendarEvent[]>);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium">Product Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-6">
            {Object.entries(productGroups).map(([product, productEvents]) => (
              <div key={product} className="space-y-2">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: getProductColor(product) }}
                  />
                  <h3 className="font-medium">{product}</h3>
                  <Badge variant="outline" className="ml-auto">
                    {productEvents.length} harvests
                  </Badge>
                </div>
                <div className="pl-5 border-l-2 space-y-3" style={{ borderColor: getProductColor(product) }}>
                  {productEvents
                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                    .map((event) => (
                      <div key={event.id} className="relative">
                        <div className="absolute -left-[21px] top-1/2 w-4 h-4 -mt-2 rounded-full bg-background border-2"
                             style={{ borderColor: getProductColor(product) }} />
                        <div className="bg-card rounded-lg p-3 shadow-sm">
                          <div className="font-medium">{event.farmName}</div>
                          <div className="text-sm text-muted-foreground">
                            {format(new Date(event.date), 'MMM d, yyyy')} • {event.quantity}kg
                          </div>
                          <Badge 
                            variant={event.status === 'planned' ? 'outline' : 
                                   event.status === 'in_progress' ? 'secondary' : 
                                   'default'}
                            className="mt-2"
                          >
                            {event.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default HarvestProductTimeline;
